import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Linkedin, Facebook, Youtube, ArrowRight } from "lucide-react";
import { LOGO_URL, COMPANY } from "@/lib/siteData";
import { FadeUp } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email.trim() || !email.includes("@")) {
            toast.error("Please enter a valid email.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/leads`, {
                name: "Newsletter Subscriber",
                email: email.trim(),
                phone: "0000000000",
                message: "Newsletter subscription",
                source: "newsletter_footer",
            });
            toast.success("Welcome. Expect editorial-only, never spam.");
            setEmail("");
        } catch {
            toast.error("Could not subscribe. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="relative border-t hairline bg-ink text-white overflow-hidden">
            {/* Oversized wordmark */}
            <FadeUp>
                <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-8">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="divider-gold" />
                        <span className="uppercase tracking-[0.32em] text-[11px] text-gold font-medium">
                            Studio
                        </span>
                    </div>
                    <h3 className="font-display text-[14vw] md:text-[10vw] leading-[0.9] tracking-tighter">
                        Feel.<br />Before you build.
                    </h3>
                </div>
            </FadeUp>

            {/* Newsletter */}
            <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-white/10">
                <div className="md:col-span-6">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-gold mb-3">Journal</div>
                    <h4 className="font-display text-3xl md:text-4xl leading-tight tracking-tight">
                        One thoughtful email a month.
                    </h4>
                    <p className="mt-3 text-white/60 text-sm max-w-md">
                        Behind-the-scenes of ongoing builds, new visualization tools, and rare
                        design essays. Unsubscribe in a click.
                    </p>
                </div>
                <form onSubmit={subscribe} data-testid="newsletter-form" className="md:col-span-6 flex flex-col md:flex-row gap-3 md:items-end">
                    <label className="flex-1 block">
                        <span className="block text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2">
                            Email
                        </span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="newsletter-email"
                            placeholder="you@studio.com"
                            className="w-full h-12 bg-transparent border-b border-white/30 focus:border-gold outline-none px-1 text-base placeholder:text-white/30"
                        />
                    </label>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="newsletter-submit"
                        className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-gold text-white font-medium hover:brightness-110 transition disabled:opacity-50"
                    >
                        {busy ? "Adding…" : "Subscribe"}
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </form>
            </div>

            {/* Directory */}
            <div className="max-w-7xl mx-auto px-5 md:px-8 py-12 grid grid-cols-1 md:grid-cols-12 gap-10 border-t border-white/10">
                <div className="md:col-span-5">
                    <div className="flex items-center gap-4">
                        <img src={LOGO_URL} alt={COMPANY.name} className="h-14 w-14 object-contain" />
                        <div>
                            <div className="font-display text-2xl tracking-tight">
                                CHANDAK <span className="text-gold">BuildVision</span>
                            </div>
                            <div className="text-xs uppercase tracking-[0.24em] text-white/60">
                                {COMPANY.tagline}
                            </div>
                        </div>
                    </div>
                    <p className="mt-5 max-w-md text-white/60 text-sm leading-relaxed">
                        Architectural visualization, interior design, and structural guidance for
                        homes, businesses, and institutions across India.
                    </p>
                    <div className="mt-6 flex items-center gap-3">
                        <FS href={COMPANY.socials.instagram}><Instagram className="h-4 w-4" /></FS>
                        <FS href={COMPANY.socials.linkedin}><Linkedin className="h-4 w-4" /></FS>
                        <FS href={COMPANY.socials.facebook}><Facebook className="h-4 w-4" /></FS>
                        <FS href={COMPANY.socials.youtube}><Youtube className="h-4 w-4" /></FS>
                    </div>
                </div>

                <div className="md:col-span-3">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-gold mb-4">Explore</div>
                    <ul className="space-y-3 text-sm text-white/80">
                        <li><a href="#about" className="link-hover">About</a></li>
                        <li><a href="#services" className="link-hover">Services</a></li>
                        <li><a href="#portfolio" className="link-hover">Portfolio</a></li>
                        <li><a href="#process" className="link-hover">Process</a></li>
                        <li><a href="#faq" className="link-hover">FAQ</a></li>
                        <li><a href="#contact" className="link-hover">Contact</a></li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-gold mb-4">Services</div>
                    <ul className="space-y-3 text-sm text-white/80">
                        <li>3D Exterior</li>
                        <li>Interior Design</li>
                        <li>Walkthroughs</li>
                        <li>VR Experience</li>
                        <li>Structural</li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-gold mb-4">Contact</div>
                    <ul className="space-y-3 text-sm text-white/80">
                        <li><a href={`tel:${COMPANY.phoneIntl}`} className="link-hover">{COMPANY.phone}</a></li>
                        <li className="break-all"><a href={`mailto:${COMPANY.email}`} className="link-hover">{COMPANY.email}</a></li>
                        <li>{COMPANY.location}</li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
                    <div>© 2026 CHANDAK BuildVision. All Rights Reserved.</div>
                    <div className="flex items-center gap-6">
                        <a href="#" className="link-hover">Privacy Policy</a>
                        <a href="#" className="link-hover">Terms &amp; Conditions</a>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FS({ href, children }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="h-10 w-10 grid place-items-center rounded-full border border-white/15 hover:border-gold hover:text-gold hover:scale-110 transition"
        >
            {children}
        </a>
    );
}
